import { useEffect, useState } from 'react'
import { useForm } from '@eversports/klimt-forms/Form'
import InviteFriendsComponent from '@eversports/user-components/InviteFriends'
import NoFriendsPlaceholder from '@eversports/user-components/InviteFriends/NoFriendsPlaceholder'
import FriendsLoadingPlaceholder from '@eversports/user-components/InviteFriends/FriendsLoadingPlaceholder'
import SuccessfullyInvitedFriends from '@eversports/user-components/InviteFriends/SuccessfullyInvitedFriends'

import useAmplitude from '@eversports/amplitude-react/useAmplitude'
import {
  useInviteFriendsMeLazyQuery,
  useInviteFriendsMeQuery,
  useInviteFriendsShareEventMutation,
} from '../../../../graphql'

import { FriendsToInviteForm } from './InviteFriends.types'
import { NUMBER_OF_INITIAL_FRIENDS_FETCHED } from './InviteFriends.constants'

interface Props {
  eventId: string
}

const InviteFriends = ({ eventId }: Props) => {
  const { amplitude } = useAmplitude()
  const [hasInvitedFriends, setHasInvitedFriends] = useState(false)
  const [getFriends, { data, called, loading }] = useInviteFriendsMeLazyQuery()
  const { data: initialData, loading: initialLoading } = useInviteFriendsMeQuery({
    variables: { first: NUMBER_OF_INITIAL_FRIENDS_FETCHED },
  })

  const [inviteFriendsToEvent, { loading: loadingEventMutation }] = useInviteFriendsShareEventMutation({
    onCompleted: () => {
      setHasInvitedFriends(true)
    },
  })

  useEffect(() => {
    if (!amplitude || !initialData?.me) return
    amplitude.logEvent('Viewed Share With Friends', {
      numberOfFriends: initialData.me.friendsCount,
      type: 'event',
    })
  }, [initialData])

  const methods = useForm<FriendsToInviteForm>({
    defaultValues: { friendsToInvite: [] },
  })

  if (!initialData || initialLoading) {
    return <FriendsLoadingPlaceholder numberOfSkeletons={NUMBER_OF_INITIAL_FRIENDS_FETCHED} />
  }

  if (initialData && initialData.me && !initialData.me.friends.edges.length) {
    const to = `/user/${initialData.me.handle}/friends?from=sharing-action`
    return <NoFriendsPlaceholder to={to} isExternal />
  }

  if (!initialData?.me) {
    return null
  }

  if (hasInvitedFriends) {
    return <SuccessfullyInvitedFriends />
  }

  const handleFetchFriends = (args: { after?: string }) => {
    getFriends({
      variables: {
        first: NUMBER_OF_INITIAL_FRIENDS_FETCHED,
        after: args.after,
      },
    })
  }

  const handleSubmit = () => {
    const userIds = methods.getValues('friendsToInvite')
    inviteFriendsToEvent({ variables: { eventId, userIds } })

    if (amplitude) {
      amplitude.logEvent('Clicked Share With Friends Button', {
        numberOfSelectedFriends: userIds.length,
        type: 'event',
      })
    }
  }

  const fetchedFriends = !called ? initialData.me.friends : data?.me?.friends

  return (
    <InviteFriendsComponent
      fetchFriends={handleFetchFriends}
      fetchedFriends={fetchedFriends}
      formMethods={methods}
      isLoading={loading}
      isSubmitting={loadingEventMutation}
      onSubmit={handleSubmit}
    />
  )
}

export default InviteFriends
